import { AvailableImage } from '~/hooks/image/usePrimaryImage';
import AppConfig from '~/utils/appconfig';
import { useCompositeCbctBackendTask } from './backend/tasks/useCompositeCbctBackendTask';
import useCompositeCbctConnectorTask from './connector/tasks/useCompositeCbctConnectorTask';

export type CompositeCbctInput = {
    cbct: AvailableImage;
    baseCt: AvailableImage;
};

export type UseCompositeCbctTask = {
    startProcessing: (input: CompositeCbctInput) => Promise<void>;
    isSubmitting: boolean;
};

/**
 * Single entry point for the Composite CBCT AI task. Abstracts over
 * the Connector vs direct-backend connectivity modes so callers (the
 * dialog) do not have to know which mode the app is running in.
 *
 * Both underlying hooks are called unconditionally to honour the
 * rules-of-hooks contract; only the active mode's hook performs work
 * on `startProcessing`.
 *
 * Input is an `AvailableImage` pair so both loaded viewport images
 * and session-only entries (`isLoaded: false`, no `imageObject`) can
 * flow through. The connector path extracts only the UIDs; the
 * backend path requires a materialised `Image3D` because it uploads
 * slice blobs directly. Session-only images are a connector-mode-
 * only concern, so the backend narrowing is safe in practice.
 */
export default function useCompositeCbctTask(
    onSuccess?: () => void
): UseCompositeCbctTask {
    const backend = useCompositeCbctBackendTask(onSuccess);
    const connector = useCompositeCbctConnectorTask(onSuccess);

    const startProcessing = async (input: CompositeCbctInput) => {
        if (AppConfig.isConnectorMode) {
            await connector.startProcessing({
                cbct: {
                    seriesInstanceUID: input.cbct.seriesInstanceUID,
                    studyInstanceUID: input.cbct.studyInstanceUID,
                },
                baseCt: {
                    seriesInstanceUID: input.baseCt.seriesInstanceUID,
                    studyInstanceUID: input.baseCt.studyInstanceUID,
                },
            });
            return;
        }

        // Direct-backend mode. The uploader reads slice blobs from the
        // local `Image3D`, so both inputs must already be materialised.
        // Session-only images do not occur in this mode — direct
        // backend has no session route — but surface a clear error if
        // the dialog ever hands us an unloaded entry by mistake.
        if (!input.cbct.imageObject || !input.baseCt.imageObject) {
            throw new Error(
                'Composite CBCT direct-backend mode requires both ' +
                    'inputs to be loaded locally as Image3D objects.'
            );
        }
        await backend.startProcessing({
            cbctImage: input.cbct.imageObject,
            baseCtImage: input.baseCt.imageObject,
        });
    };

    return {
        startProcessing,
        isSubmitting: AppConfig.isConnectorMode
            ? connector.isSubmitting
            : backend.isSubmitting,
    };
}
